// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { enablePostalCodeLocationFinder } from "@bg/App/Store/Selectors/PostalCodeSelector";
import { LinkPresentationProps } from "@bg/MobiusOverrides/Link/Link";
import { constants } from "@bg/XDL/constants";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { hasLocalStorage } from "@bg/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import React, { Fragment } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    isLoading?: boolean;
    /**
     * If true, displays an empty cart total UI.
     * If false, displays amounts from the cart.
     * Default value: false
     */
    isCartEmpty?: boolean;
    /**
     * If true, displays tax, shipping and handling, and total amounts.
     * If false, does not display tax, shipping and handling, or total amounts.
     * Default value: true
     */
    showTaxAndShipping?: boolean;
    cart?: Cart;
    orderPromotions?: PromotionModel[];
    shippingPromotions?: PromotionModel[];
    discountTotal?: number;
    extendedStyles?: CartTotalDisplayStyles;
    canModifyOrder?: boolean;
    // XNG-Changes
    showTaxMessage?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    showPricing: state.context.showPricing,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
    // Xng-Change
    cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
    isAuth: getSession(state).isAuthenticated,
    enablePostalCodeLocationFinder: enablePostalCodeLocationFinder(state),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface CartTotalDisplayStyles {
    container?: GridContainerProps;
    labelGridItem?: GridItemProps;
    valueGridItem?: GridItemProps;
    subtotalLabel?: TypographyProps;
    subtotalValue?: TypographyProps;
    promotionLabel?: TypographyProps;
    promotionValue?: TypographyProps;
    discountsLabel?: TypographyProps;
    discountsValue?: TypographyProps;
    shippingAndHandlingLabel?: TypographyProps;
    shippingAndHandlingValue?: TypographyProps;
    otherChargesLabel?: TypographyProps;
    otherChargesValue?: TypographyProps;
    taxLabel?: TypographyProps;
    taxValue?: TypographyProps;
    totalLabel?: TypographyProps;
    totalValue?: TypographyProps;
    loadingSpinner?: LoadingSpinnerProps;
    titleGridItem?: GridItemProps;
    title?: TypographyProps;
    totalWrapper?: GridContainerProps;
    totalGridItem?: GridItemProps;
    editCartLink?: LinkPresentationProps;
    editCart?: TypographyProps;
    // XNG-Changes
    taxMessageGridItem?: GridItemProps;
    taxMessage?: TypographyProps;
}

export const cartTotalDisplayStyles: CartTotalDisplayStyles = {
    container: {
        gap: 10,
        offsetCss: css`
            &&&& {
                margin: 0;
                width: 100%;
            }
        `,
    },
    labelGridItem: {
        width: 6,
        css: css`
            padding-left: 0px;
        `,
    },
    valueGridItem: {
        width: 6,
    },
    subtotalLabel: {
        size: fontConfig.fontSize.regular,
        weight: fontConfig.weight.bold,
        lineHeight: "18px",
        css: css`
            margin-top: 6px;
        `,
    },
    subtotalValue: {
        size: fontConfig.fontSize.regular,
        weight: fontConfig.weight.bold,
        lineHeight: "18px",
        css: css`
            margin-top: 6px;
            margin-left: auto;
        `,
    },
    promotionValue: {
        css: css`
            margin-left: auto;
        `,
    },
    discountsValue: {
        css: css`
            margin-left: auto;
        `,
    },
    shippingAndHandlingValue: {
        css: css`
            margin-left: auto;
        `,
    },
    otherChargesValue: {
        css: css`
            margin-left: auto;
        `,
    },
    taxValue: {
        css: css`
            margin-left: auto;
        `,
    },
    totalLabel: {
        size: 18,
        lineHeight: fontConfig.lineHeight.xSmall,
        weight: fontConfig.weight.bold,
    },
    totalValue: {
        size: 18,
        lineHeight: fontConfig.lineHeight.xSmall,
        weight: fontConfig.weight.bold,
        css: css`
            margin-left: auto;
        `,
    },
    loadingSpinner: {
        size: 15,
        css: css`
            margin-top: auto;
            margin-bottom: auto;
            margin-left: auto;
        `,
    },
    titleGridItem: {
        css: css`
            padding: 12px 0px;
            border-top: 2px solid ${getColor("common.ui3")};
            border-bottom: 2px solid ${getColor("common.ui3")};
        `,
    },
    title: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
        `,
    },
    totalWrapper: {
        gap: 10,
        css: css`
            margin-top: 20px;
            padding: 20px 0;
            border-top: 2px solid ${getColor("common.ui3")};
        `,
    },
    totalGridItem: {
        width: 6,
    },
    editCartLink: {
        css: css`
            margin-left: auto;
        `,
    },
    editCart: {
        size: fontConfig.fontSize.regular,
        color: "secondary",
        lineHeight: fontConfig.lineHeight.singleLineSmall,
        weight: fontConfig.weight.semiBold,
        css: css`
            letter-spacing: 0.2px;
            opacity: 0.8;
        `,
    },
};

const CartTotalDisplay = ({
    isCartEmpty = false,
    showTaxAndShipping = true,
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    isLoading,
    enableVat,
    showPricing,
    isCheckoutReviewAndSubmitPage,
    cartPageUrl,
    canModifyOrder,
    extendedStyles,
    // XNG-Changes
    isAuth,
    enablePostalCodeLocationFinder,
    showTaxMessage = false,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(cartTotalDisplayStyles, extendedStyles));
    const [customShowPricing, setCustomShowPricing] = React.useState(false);

    React.useEffect(() => {
        if (
            (enablePostalCodeLocationFinder && !hasLocalStorage(constants.localStorage.POSTAL_CODE_ID) && !isAuth) ||
            !showPricing
        ) {
            setCustomShowPricing(false);
        } else {
            setCustomShowPricing(true);
        }
    }, []);

    if (!showPricing && !isCheckoutReviewAndSubmitPage) {
        return null;
    }

    /**
     * XNG-Changes
     * Spinner added to the wraping container - widget
     * No need for spinner here
     * just return null
     */
    if (isLoading || !cart) {
        return null;
    }

    if (isCartEmpty) {
        return (
            <GridContainer {...styles.container}>
                {showTaxAndShipping && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.totalValue}>{`${cart.currencySymbol} -`}</Typography>
                        </GridItem>
                    </>
                )}
                {!showTaxAndShipping && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.subtotalLabel}>
                                {translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.subtotalValue}>{`${cart.currencySymbol} -`}</Typography>
                        </GridItem>
                    </>
                )}
            </GridContainer>
        );
    }

    const filteredOrderPromotions = orderPromotions?.filter(o => o.amount > 0);
    const filteredShippingPromotions = shippingPromotions?.filter(o => o.amount > 0);

    const ecoFees = cart?.properties?.ecoFees ? cart.properties.ecoFees : "";
    const showNA = !customShowPricing || cart.cartNotPriced;
    const canModifyOrderOfCart = cart.type === "Quote" ? false : canModifyOrder;

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.titleGridItem} width={canModifyOrderOfCart ? 9 : 12}>
                <Typography {...styles.title}>{translate("Order Summary")}</Typography>
            </GridItem>
            {canModifyOrderOfCart && (
                <GridItem {...styles.titleGridItem} width={3}>
                    <Link {...styles.editCartLink} href={cartPageUrl}>
                        <Typography {...styles.editCart}>{translate("Edit Cart")}</Typography>
                    </Link>
                </GridItem>
            )}
            <GridItem {...styles.labelGridItem}>
                <Typography {...styles.subtotalLabel} {...{ showTaxAndShipping }}>
                    {translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                </Typography>
            </GridItem>
            <GridItem {...styles.valueGridItem}>
                <Typography
                    {...styles.subtotalValue}
                    {...{ showTaxAndShipping }}
                    data-test-selector="cartTotal_subTotal"
                >
                    {showNA ? translate("N/A") : cart.orderSubTotalDisplay}
                </Typography>
            </GridItem>
            {filteredOrderPromotions &&
                filteredOrderPromotions.map(promotion => (
                    <Fragment key={promotion.id}>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.promotionLabel} data-test-selector="cartTotal_orderPromotionName">
                                {`${translate("Promotion")}: ${promotion.name}`}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.promotionValue} data-test-selector="cartTotal_orderPromotionValue">
                                {`-${promotion.amountDisplay}`}
                            </Typography>
                        </GridItem>
                    </Fragment>
                ))}
            {showTaxAndShipping && cart.shippingAndHandlingDisplay.length > 0 && (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.shippingAndHandlingLabel}>{translate("Shipping & Handling")}</Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography
                            {...styles.shippingAndHandlingValue}
                            data-test-selector="cartTotal_shippingAndHandlingAmount"
                        >
                            {/* XNG-Changes: BG-3035 */}
                            {cart.shippingAndHandling === 0 && cart.carrier?.id === cart.properties.localDeliveryId
                                ? translate("TBD")
                                : cart.shippingAndHandlingDisplay}
                        </Typography>
                    </GridItem>
                </>
            )}
            {filteredShippingPromotions &&
                filteredShippingPromotions.map(promotion => (
                    <Fragment key={promotion.id}>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.promotionLabel}>
                                {`${translate("Promotion")}: ${promotion.name}`}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography
                                {...styles.promotionValue}
                                data-test-selector="cartTotal_shippingPromotionValue"
                            >
                                {`-${promotion.amountDisplay}`}
                            </Typography>
                        </GridItem>
                    </Fragment>
                ))}
            {showTaxAndShipping && cart.totalTaxDisplay.length > 0 && cart.customerOrderTaxes!.length === 0 && (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.taxLabel}>{enableVat ? translate("VAT") : translate("Tax")}</Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography {...styles.taxValue} data-test-selector="cartTotal_totalTaxDisplay">
                            {cart.totalTaxDisplay}
                        </Typography>
                    </GridItem>
                    {/* XNG-Changes */}
                    {showTaxMessage && cart?.paymentMethod && !cart.paymentMethod.isCreditCard && (
                        <GridItem {...styles.taxMessageGridItem}>
                            <Typography {...styles.taxMessage}>
                                {siteMessage("Custom_Estimated_Tax_Amount_Message")}
                            </Typography>
                        </GridItem>
                    )}
                </>
            )}
            {ecoFees && (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.taxLabel}>{translate("Eco Fees")}</Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography {...styles.taxValue} data-test-selector="cartTotal_ecoFeesDisplay">
                            {ecoFees}
                        </Typography>
                    </GridItem>
                </>
            )}
            {showTaxAndShipping &&
                [...cart.customerOrderTaxes!]
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map(tax => {
                        const key = `${tax.taxCode}_${tax.sortOrder}`;
                        return (
                            <Fragment key={key}>
                                <GridItem {...styles.labelGridItem}>
                                    <Typography>{tax.taxDescription || translate("Tax")}</Typography>
                                </GridItem>
                                <GridItem {...styles.valueGridItem}>
                                    <Typography {...styles.taxValue}>{tax.taxAmountDisplay}</Typography>
                                </GridItem>
                            </Fragment>
                        );
                    })}
            {discountTotal !== undefined && discountTotal > 0 && (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.discountsLabel}>{translate("Discounts")}</Typography>
                    </GridItem>
                    <GridItem
                        {...styles.valueGridItem}
                        css={css`
                            flex-direction: column;
                        `}
                        data-test-selector="cartTotal_totalDiscounts"
                    >
                        <Typography {...styles.discountsValue}>{`${translate("You saved")} `}</Typography>
                        <Typography {...styles.discountsValue}>
                            <LocalizedCurrency amount={discountTotal} currencySymbol={cart.currencySymbol} />
                        </Typography>
                    </GridItem>
                </>
            )}
            {showTaxAndShipping && (
                <GridContainer {...styles.totalWrapper}>
                    <GridItem {...styles.totalGridItem}>
                        <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography {...styles.totalValue} data-test-selector="cartTotal_orderGrandTotalDisplay">
                            {cart.orderGrandTotalDisplay}
                        </Typography>
                    </GridItem>
                </GridContainer>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps)(CartTotalDisplay);
